
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import {
    startBooking,
    getPaymentOptions as getPaymentOptionsBF,
    getPaymentOptionDetail as getPaymentOptionDetailBF,
    beginPaymentTransaction as beginPaymentTransactionBF,
    confirmBooking
} from '../services/bookingService';
import {
    getContents,
    getContentDetail
} from '../services/tourvisioService';
import type { BookingRequest, ApiLogEntry, PaymentGroup } from '../types';

interface PaymentPageProps {
    logApiCall: (log: ApiLogEntry) => void;
}

export const PaymentPage: React.FC<PaymentPageProps> = ({ logApiCall }) => {
    const { t, culture, currency: contextCurrency } = useLocalization();
    const navigate = useNavigate();
    const { state } = useLocation();
    const bookingRequest = state?.bookingRequest as BookingRequest;
    const sessionId = state?.sessionId as string | undefined;

    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [error, setError] = useState<string | null>(null);

    const [transactionId, setTransactionId] = useState<string | null>(null);
    const [ccDetails, setCcDetails] = useState<PaymentGroup[]>([]);

    // Stores which PaymentOption (Group ID) a specific PaymentType belongs to
    const [paymentTypeMap, setPaymentTypeMap] = useState<Record<string, any>>({});

    const [selectedPaymentType, setSelectedPaymentType] = useState<string>('');
    const [selectedInstallment, setSelectedInstallment] = useState<number>(1);

    // Terms
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [termsContent, setTermsContent] = useState<string>('');

    const hasInitialized = useRef(false);

    // Persist booking context to localStorage for post-redirect recovery
    const persistBookingContext = () => {
        if (!bookingRequest) return;
        const context = {
            hotelName: bookingRequest.hotel?.name || '',
            hotelId: bookingRequest.hotel?.id || '',
            hotelTourvisioId: bookingRequest.hotel?.tourvisioId || '',
            totalPrice: bookingRequest.offer?.price || 0,
            currency: bookingRequest.offer?.currency || 'EUR',
            checkIn: bookingRequest.offer?.checkIn || '',
            checkOut: bookingRequest.offer?.checkOut || '',
            roomType: bookingRequest.offer?.roomType || '',
            boardType: bookingRequest.offer?.boardType || '',
            nights: bookingRequest.offer?.nights || 0,
            offerId: bookingRequest.offer?.id || '',
            contactEmail: bookingRequest.contact?.email || '',
            contactPhone: bookingRequest.contact?.phone || '',
            city: bookingRequest.hotel?.city || '',
            country: bookingRequest.hotel?.country || '',
            passengers: bookingRequest.passengers || [],
            flights: bookingRequest.flights || [],
            transactionId: transactionId || '',
            sessionId: sessionId || ''
        };
        try {
            localStorage.setItem('lumitours_booking_context', JSON.stringify(context));
            console.log('💾 Booking context saved to localStorage');
        } catch (e) {
            console.warn('Failed to save booking context:', e);
        }
    };

    const initTransaction = async () => {
        setError(null);
        setIsLoading(true);
        try {
            // 1. Ensure we have a sessionId — use the new bookingFlow API
            setStatusMessage(t('payment_connecting'));

            let currentSessionId = sessionId;
            if (!currentSessionId) {
                // Start a new booking session via bookingFlow
                const session = await startBooking(
                    [bookingRequest.offer.id],
                    contextCurrency,
                    undefined,
                    culture
                );
                currentSessionId = session.sessionId;
                console.log(`✅ New booking session created: ${currentSessionId}`);
            }
            // Store sessionId for handlePayment to use
            setTransactionId(currentSessionId); // reusing transactionId state for sessionId

            // 2. Get Payment Options (via bookingFlow API)
            setStatusMessage(t('payment_fetching_methods'));
            const optionIds = await getPaymentOptionsBF(currentSessionId);

            if (!optionIds || optionIds.length === 0) {
                throw new Error("No payment methods are currently available for this reservation.");
            }

            // 3. Fetch Details for ALL returned options
            const allGroups: PaymentGroup[] = [];
            const typeMapping: Record<string, any> = {};

            for (const optId of optionIds) {
                try {
                    const detailResult = await getPaymentOptionDetailBF(currentSessionId, Number(optId));
                    // bookingService returns options array or wrapped object
                    const groups = Array.isArray(detailResult) ? detailResult as unknown as PaymentGroup[] : [detailResult] as unknown as PaymentGroup[];
                    if (groups) {
                        groups.forEach((group: any) => {
                            if (!group || !group.options) return;
                            
                            // Filter out "Balance" / "Bakiye" payment options
                            group.options.forEach((subOpt: any) => {
                                if (!subOpt.paymentTypes) return;
                                subOpt.paymentTypes = subOpt.paymentTypes.filter((pt: any) => {
                                    const ptName = (pt.name || '').toLowerCase();
                                    return !ptName.includes('balance') && !ptName.includes('bakiye');
                                });
                                
                                subOpt.paymentTypes.forEach((pt: any) => {
                                    typeMapping[pt.id] = optId;
                                });
                            });
                            
                            // Clean up empty options
                            group.options = group.options.filter((subOpt: any) => subOpt.paymentTypes && subOpt.paymentTypes.length > 0);
                            
                            if (group.options.length > 0) {
                                allGroups.push(group);
                            }
                        });
                    }
                } catch (e) {
                    console.warn(`Failed to load details for option ${optId}`, e);
                }
            }

            if (allGroups.length === 0) {
                throw new Error("Failed to load payment details from gateways.");
            }

            setCcDetails(allGroups);
            setPaymentTypeMap(typeMapping);

            // Select first available payment type by default
            if (allGroups.length > 0 && allGroups[0].options.length > 0 && allGroups[0].options[0].paymentTypes.length > 0) {
                setSelectedPaymentType(allGroups[0].options[0].paymentTypes[0].id);
            }

        } catch (error) {
            console.error(error);
            setError(error instanceof Error ? error.message : "Failed to initialize payment system.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTerms = async () => {
        try {
            // First list all available content items from TourVisio
            const contents = await getContents(logApiCall);
            console.log('📋 Available content codes:', contents.map((c: any) => c.code || c.name));

            // Find the terms content code (could be 'TermsAndConditions', 'TermsOfUse', etc.)
            const termsItem = contents.find((c: any) => {
                const code = (c.code || '').toLowerCase();
                return code.includes('terms') || code.includes('conditions') || code.includes('contract');
            });

            if (termsItem) {
                const content = await getContentDetail(termsItem.code, logApiCall, culture);
                setTermsContent(content || '<p>Terms and conditions content not available.</p>');
            } else {
                // Fallback: try common codes directly
                const content = await getContentDetail('TermsAndConditions', logApiCall, culture);
                setTermsContent(content || '<p>Terms and conditions content not available.</p>');
            }
        } catch (e) {
            console.warn('Failed to load terms:', e);
            setTermsContent('<p>Terms and conditions could not be loaded.</p>');
        }
    };

    useEffect(() => {
        if (!bookingRequest) {
            navigate('/');
            return;
        }

        if (hasInitialized.current) return;
        hasInitialized.current = true;

        initTransaction();
        fetchTerms();
    }, [bookingRequest, navigate, logApiCall]);

    const finalizeBooking = async () => {
        if (!transactionId) return;
        setStatusMessage(t('payment_confirming'));
        try {
            // Use bookingFlow /confirm endpoint (session-based)
            const result = await confirmBooking(transactionId);
            navigate('/confirmation', { state: { reservation: {
                reservationNumber: result.reservationNumber,
                status: 'Confirmed',
                totalPrice: bookingRequest.offer.price,
                currency: bookingRequest.offer.currency,
                hotelName: bookingRequest.hotel.name,
                checkIn: bookingRequest.offer.checkIn,
                checkOut: bookingRequest.offer.checkOut || ''
            } } });
        } catch (error) {
            alert("Payment successful but final confirmation failed. Please contact support with Session ID: " + transactionId);
            setIsProcessing(false);
        }
    };

    const handlePayment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!transactionId || !selectedPaymentType) return;

        setIsProcessing(true);
        setStatusMessage(t('payment_processing'));

        // Persist booking context BEFORE redirect (localStorage survives external navigation)
        persistBookingContext();

        try {
            // Determine the correct PaymentOption ID (e.g. 18 or 3) for the selected card type
            const parentOptionId = paymentTypeMap[selectedPaymentType];
            if (!parentOptionId) {
                throw new Error("Invalid payment configuration selected.");
            }

            // transactionId here is actually the sessionId (set during initTransaction)
            const currentSessionId = transactionId;

            const successUrl = `${window.location.origin}/confirmation?paymentStatus=0&tid=${currentSessionId}`;
            const errorUrl = `${window.location.origin}/payment?paymentStatus=1&tid=${currentSessionId}`;

            // Use bookingFlow /begin-payment endpoint (session-based, persists context)
            // This ensures the session is stored in PersistentTransactionStore
            // so finalize-after-payment can find it after the redirect.
            const response = await beginPaymentTransactionBF({
                sessionId: currentSessionId,
                paymentOption: Number(parentOptionId),
                successUrl,
                errorUrl,
                paymentTypeId: selectedPaymentType,
                installment: selectedInstallment,
                culture,
                // Include booking context for post-redirect recovery
                // (backend stores this in session for finalize-after-payment)
                bookingContext: {
                    hotelName: bookingRequest.hotel?.name || '',
                    hotelId: bookingRequest.hotel?.id || '',
                    hotelTourvisioId: bookingRequest.hotel?.tourvisioId || '',
                    totalPrice: bookingRequest.offer?.price || 0,
                    currency: bookingRequest.offer?.currency || 'EUR',
                    checkIn: bookingRequest.offer?.checkIn || '',
                    checkOut: bookingRequest.offer?.checkOut || '',
                    roomType: bookingRequest.offer?.roomType || '',
                    boardType: bookingRequest.offer?.boardType || '',
                    nights: bookingRequest.offer?.nights || 0,
                    offerId: bookingRequest.offer?.id || '',
                    contactEmail: bookingRequest.contact?.email || '',
                    contactPhone: bookingRequest.contact?.phone || '',
                    passengers: bookingRequest.passengers || [],
                    flights: bookingRequest.flights || []
                }
            } as any);
            console.log('💳 Payment response keys:', Object.keys(response || {}));
            console.log('💳 Full payment response:', JSON.stringify(response, null, 2));

            // Persist paymentTransactionId to localStorage for post-redirect recovery
            if (response?.paymentTransactionId) {
                try {
                    const currentContextStr = localStorage.getItem('lumitours_booking_context');
                    if (currentContextStr) {
                        const currentContext = JSON.parse(currentContextStr);
                        currentContext.paymentTransactionId = response.paymentTransactionId;
                        localStorage.setItem('lumitours_booking_context', JSON.stringify(currentContext));
                        console.log('💾 Updated booking context with paymentTransactionId:', response.paymentTransactionId);
                    }
                } catch (e) {
                    console.warn('Failed to update booking context with paymentTransactionId:', e);
                }
            }

            // Safely extract body — TourVisio sometimes wraps in body, sometimes at root
            const paymentBody = response?.body || null;
            const paymentHtml = paymentBody?.html || response?.html || null;
            const postUrl = paymentBody?.postUrl || response?.postUrl || null;
            const isSuccess = response?.header?.success || response?.success || false;

            console.log('💳 paymentHtml?', !!paymentHtml, 'postUrl?', postUrl, 'isSuccess?', isSuccess);

            // Handle various payment transaction types from TourVisio
            if (paymentHtml) {
                setStatusMessage(t('payment_redirecting'));

                // TourVisio returns HTML with Stripe redirect scripts.
                // Instead of unreliable innerHTML script injection, we parse
                // the Stripe session ID and key, then redirect properly.
                const sessionMatch = paymentHtml.match(/sessionId:\s*['"]([^'"]+)['"]/);
                const keyMatch = paymentHtml.match(/Stripe\(['"]([^'"]+)['"]\)/);

                if (sessionMatch && keyMatch) {
                    // Stripe checkout flow — load Stripe.js first, then redirect
                    const stripeSessionId = sessionMatch[1];
                    const stripePublicKey = keyMatch[1];
                    console.log('💳 Parsed Stripe session:', stripeSessionId);

                    const loadStripe = (): Promise<any> => {
                        return new Promise((resolve, reject) => {
                            if ((window as any).Stripe) {
                                resolve((window as any).Stripe(stripePublicKey));
                                return;
                            }
                            const existing = document.querySelector('script[src*="js.stripe.com"]');
                            if (existing) {
                                // Script tag exists but Stripe not ready — wait for it
                                existing.addEventListener('load', () => resolve((window as any).Stripe(stripePublicKey)));
                                existing.addEventListener('error', () => reject(new Error('Stripe.js failed to load')));
                                return;
                            }
                            const script = document.createElement('script');
                            script.src = 'https://js.stripe.com/v3/';
                            script.onload = () => resolve((window as any).Stripe(stripePublicKey));
                            script.onerror = () => reject(new Error('Failed to load Stripe.js'));
                            document.head.appendChild(script);
                        });
                    };

                    try {
                        const stripe = await loadStripe();
                        const { error: stripeError } = await stripe.redirectToCheckout({ sessionId: stripeSessionId });
                        if (stripeError) {
                            throw new Error(stripeError.message);
                        }
                    } catch (stripeErr) {
                        console.error('Stripe redirect failed:', stripeErr);
                        setError('Payment redirect failed: ' + (stripeErr instanceof Error ? stripeErr.message : 'Unknown error'));
                        setIsProcessing(false);
                    }
                } else {
                    // Non-Stripe flow: might be a 3D Secure form or other gateway HTML
                    const container = document.createElement('div');
                    container.innerHTML = paymentHtml;
                    document.body.appendChild(container);

                    const form = container.querySelector('form');
                    if (form) {
                        form.submit();
                    } else {
                        // Fallback: try to execute scripts sequentially
                        const scripts = Array.from(container.querySelectorAll('script'));
                        for (const s of scripts) {
                            const newScript = document.createElement('script');
                            if (s.src) {
                                newScript.src = s.src;
                                await new Promise<void>((resolve) => {
                                    newScript.onload = () => resolve();
                                    newScript.onerror = () => resolve();
                                    document.head.appendChild(newScript);
                                });
                            } else if (s.textContent) {
                                newScript.textContent = s.textContent;
                                document.body.appendChild(newScript);
                            }
                        }
                    }
                }
            } else if (postUrl) {
                setStatusMessage(t('payment_redirecting'));
                window.location.href = postUrl;
            } else if (isSuccess) {
                await finalizeBooking();
            } else {
                // Check if the API returned an error message
                const errorMsg = response?.header?.messages?.[0]?.message
                    || response?.message
                    || response?.error
                    || "Payment provider returned an unexpected response. Please try again.";
                console.error('💳 Unexpected payment response:', JSON.stringify(response, null, 2));
                throw new Error(errorMsg);
            }

        } catch (error) {
            console.error("Payment Error:", error);
            alert("Payment failed: " + (error instanceof Error ? error.message : "Unknown error"));
            setIsProcessing(false);
        }
    };

    if (!bookingRequest) return null;

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <svg className="animate-spin h-10 w-10 text-black mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600 font-medium">{statusMessage || t('payment_initializing')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md">
                    <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{t('payment_connection_issue')}</h2>
                    <p className="text-gray-500 mb-6">{error}</p>
                    <div className="flex gap-3 justify-center">
                        <button onClick={() => navigate('/')} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">{t('cancel')}</button>
                        <button onClick={() => { hasInitialized.current = false; initTransaction(); }} className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800">{t('payment_retry')}</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen py-12 flex items-center justify-center">
            <div className="container mx-auto px-4 max-w-lg">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    <div className="bg-gray-900 p-6 text-center">
                        <h2 className="text-2xl font-bold text-white mb-2">{t('secure_payment')}</h2>
                        <p className="text-gray-400 text-sm">Transaction ID: {transactionId}</p>
                    </div>

                    <div className="p-8">
                        <div className="mb-8 text-center">
                            <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold mb-1">{t('total_amount')}</p>
                            <p className="text-4xl font-extrabold text-gray-900">{bookingRequest.offer.price.toLocaleString()} <span className="text-lg text-gray-500">{bookingRequest.offer.currency}</span></p>
                        </div>

                        <form onSubmit={handlePayment} className="space-y-5">
                            {ccDetails.length > 0 ? (
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t('payment_select_method')}</label>
                                    <select
                                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-black focus:ring-2 focus:ring-black outline-none"
                                        value={selectedPaymentType}
                                        onChange={e => setSelectedPaymentType(e.target.value)}
                                    >
                                        {ccDetails.map(group => (
                                            <optgroup key={group.id} label={group.name}>
                                                {group.options.map(opt => (
                                                    opt.paymentTypes.map(pt => (
                                                        <option key={pt.id} value={pt.id}>
                                                            {opt.name} - {pt.name}
                                                        </option>
                                                    ))
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="text-center text-red-600 bg-red-50 p-3 rounded-lg text-sm">
                                    {t('payment_no_methods')}
                                </div>
                            )}

                            <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-600">
                                <p>{t('payment_redirect_info')}</p>
                            </div>

                            {/* Terms Checkbox */}
                            <div className="flex items-start gap-3">
                                <div className="flex items-center h-5">
                                    <input
                                        id="terms"
                                        type="checkbox"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                                    />
                                </div>
                                <div className="text-sm">
                                    <label htmlFor="terms" className="font-medium text-gray-700">
                                        {t('payment_accept_terms_prefix')}{' '}
                                        <button
                                            type="button"
                                            onClick={() => setShowTerms(true)}
                                            className="text-black underline font-bold"
                                        >
                                            {t('payment_terms_title')}
                                        </button>
                                    </label>
                                </div>
                            </div>

                            {/* Terms Modal */}
                            {showTerms && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl">
                                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                            <h3 className="text-xl font-bold">{t('payment_terms_title')}</h3>
                                            <button
                                                type="button"
                                                onClick={() => setShowTerms(false)}
                                                className="p-2 hover:bg-gray-100 rounded-full"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <div className="p-6 overflow-y-auto flex-1 prose prose-sm max-w-none">
                                            <div dangerouslySetInnerHTML={{ __html: termsContent }} />
                                        </div>
                                        <div className="p-6 border-t border-gray-100 text-right">
                                            <button
                                                type="button"
                                                onClick={() => { setTermsAccepted(true); setShowTerms(false); }}
                                                className="px-6 py-2 bg-black text-white rounded-lg font-medium"
                                            >
                                                {t('payment_accept_close')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isProcessing || !selectedPaymentType || !termsAccepted}
                                className="w-full bg-black text-white font-bold py-4 rounded-xl shadow-lg hover:bg-gray-800 transition-all transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-wait mt-4"
                            >
                                {isProcessing ? t('payment_redirecting_short') : t('payment_proceed')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
